import { useEffect, useState, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Upload, FileText, Image as ImageIcon, Clock, CheckCircle2, XCircle, ShieldCheck, Loader2, Link2, Calendar, Scale } from "lucide-react";

const TYPES = [
  { id: "identity", label: "Identity", hint: "Screenshot of your PRO profile (BMI/ASCAP/SESAC), Muso.AI, or any public producer page that shows your name." },
  { id: "metadata", label: "Metadata", hint: "Accepts links or screenshots from Muso.ai (Most Popular), Jaxsta (Official), AllMusic, Genius, MusicBrainz, Discogs, and IMDb as valid proof of credits." },
  { id: "royalty",  label: "Royalty",  hint: "PRO statement, SoundExchange registration, royalty distributor screenshot." },
  { id: "rights_protection", label: "Rights Protection", hint: "Chain of Title — DAW session, social disclosure post, copyright filing, or commercial ISRC issuance. Powers your Forensic Timeline." },
];

const RIGHTS_CATEGORIES = [
  { id: "daw_creation",       label: "DAW Creation" },
  { id: "social_disclosure",  label: "Social Disclosure" },
  { id: "copyright_filing",   label: "Copyright Filing" },
  { id: "commercial_isrc",    label: "Commercial ISRC" },
];

const STATUS_META = {
  pending:  { Icon: Clock,        cls: "text-amber-400", label: "Pending review" },
  approved: { Icon: CheckCircle2, cls: "text-emerald-400", label: "Approved" },
  rejected: { Icon: XCircle,      cls: "text-rose-400",  label: "Rejected" },
};

export default function EvidenceVault({ onUpload = null, encryptBefore = null, showChainOfTitle = false } = {}) {
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [type, setType] = useState("identity");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [proofLink, setProofLink] = useState("");
  const [proofCategory, setProofCategory] = useState("daw_creation");
  const [proofDate, setProofDate] = useState("");
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/evidence");
      setSubmissions(data.submissions || []);
    } catch { /* noop */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Listen for "Fix Now" clicks from bounty cards so the correct evidence
  // type is pre-selected the moment the user lands here.
  useEffect(() => {
    const handler = (e) => {
      const t = e?.detail?.type;
      if (t && ["identity", "metadata", "royalty", "rights_protection"].includes(t)) {
        setType(t);
      }
    };
    window.addEventListener("bc-fix-now", handler);
    return () => window.removeEventListener("bc-fix-now", handler);
  }, []);

  const submit = async () => {
    const link = proofLink.trim();
    if (!file && !link) { toast.error("Attach a file or paste a proof link."); return; }
    if (link && !/^https?:\/\//i.test(link)) { toast.error("Proof link must start with http:// or https://"); return; }
    if (file && file.size > 50 * 1024 * 1024) { toast.error("Max 50 MB."); return; }
    if (type === "rights_protection" && !proofDate) { toast.error("Pick a date for the Chain of Title."); return; }
    setUploading(true);
    try {
      let file_url = "", file_name = "", file_size = 0;
      if (file) {
        // Sovereign Vault: if an encryptor was provided (via the /vault
        // page), the plaintext is transformed into an AES-GCM
        // ciphertext blob BEFORE it hits storage. Filename is prefixed
        // so we can distinguish encrypted uploads server-side.
        let payload = file;
        if (typeof encryptBefore === "function") {
          const encrypted = await encryptBefore(file);
          payload = new File([encrypted], `enc_${file.name}.aes256gcm`, { type: "application/octet-stream" });
        }
        const fd = new FormData();
        fd.append("file", payload);
        const { data: up } = await api.post("/evidence/upload-url", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        file_url = up.file_url;
        file_name = up.file_name;
        file_size = up.size;
        // Notify the parent (Vault page) so it can append to the
        // immutable ledger with the SHA-256 of the plaintext + the
        // storage URL of the ciphertext.
        if (typeof onUpload === "function") {
          try {
            await onUpload(file, {
              kind: type,
              file_url,
              encrypted: !!encryptBefore,
            });
          } catch { /* best-effort ledger append */ }
        }
      }
      await api.post("/evidence", {
        type,
        description,
        file_url,
        file_name,
        file_size,
        proof_link: link,
        ...(type === "rights_protection" ? {
          proof_category: proofCategory,
          proof_date: proofDate,
        } : {}),
      });
      toast.success("Evidence submitted. An admin will review shortly.");
      setFile(null); setDescription(""); setProofLink(""); setProofDate("");
      if (inputRef.current) inputRef.current.value = "";
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setUploading(false); }
  };

  if (!user) return null;

  return (
    <div id="evidence" data-testid="evidence-vault" className="border border-white/5 bg-[#0c0c0c] p-7">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-2">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck size={16} className="text-amber-500" />
            <div className="text-overline">Verified by Evidence</div>
          </div>
          <p className="text-sm text-neutral-400 max-w-2xl">
            Just a simple screenshot is enough — your PRO profile, Muso.AI page, royalty dashboard, or any public producer page that shows your name. An admin reviews and stamps your BeatCredsi Passport. Nothing invasive, no government ID required.
          </p>
        </div>
      </div>

      {/* UPLOAD FORM */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/5 border border-white/5">
        <div className="lg:col-span-1 bg-[#0A0A0A] p-5">
          <label className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block font-mono-tech">1. Evidence Type</label>
          <div className="space-y-2">
            {TYPES.map((t) => (
              <button key={t.id}
                data-testid={`evidence-type-${t.id}`}
                onClick={() => setType(t.id)}
                className={`w-full text-left px-3 py-2.5 border ${type === t.id ? "border-amber-500/60 bg-amber-500/5" : "border-white/10 hover:border-white/25"} transition-colors`}>
                <div className={`text-sm font-semibold ${type === t.id ? "text-amber-500" : "text-white"}`}>{t.label}</div>
                <div className="text-xs text-neutral-500 mt-1 leading-relaxed">{t.hint}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 bg-[#0A0A0A] p-5">
          <label className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block font-mono-tech">2. Upload File (PNG, JPG, WEBP, PDF · max 10 MB)</label>
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="evidence-file-input"
            className="w-full bg-[#0c0c0c] border border-white/15 px-3 py-2.5 text-sm text-neutral-300 file:mr-3 file:bg-amber-500 file:text-black file:border-0 file:px-3 file:py-1 file:text-xs file:font-semibold file:cursor-pointer cursor-pointer"
          />
          {file && (
            <div className="mt-2 text-[13px] font-mono-tech text-neutral-400 flex items-center gap-2">
              {file.type === "application/pdf" ? <FileText size={12} /> : <ImageIcon size={12} />}
              {file.name} · {(file.size / 1024).toFixed(0)} KB
            </div>
          )}

          {/* Proof Link — primary evidence for creators who prefer URLs */}
          <label className="mt-4 mb-2 flex items-center gap-1.5 text-xs uppercase tracking-widest text-neutral-500 font-mono-tech" htmlFor="evidence-proof-link">
            <Link2 size={12} className="text-amber-400" /> Or paste a Proof Link (URL)
          </label>
          <input
            id="evidence-proof-link"
            type="url"
            value={proofLink}
            onChange={(e) => setProofLink(e.target.value)}
            placeholder="https://credits.muso.ai/…  ·  https://jaxsta.com/…  ·  https://allmusic.com/…"
            data-testid="evidence-proof-link"
            className="w-full bg-[#0c0c0c] border border-white/15 px-3 py-2.5 text-sm font-mono-tech text-white outline-none focus:border-amber-500 placeholder:text-neutral-600"
          />
          <p className="mt-1.5 text-[11px] text-neutral-500 leading-relaxed">
            Paste a link from <span className="text-amber-400">Muso.ai</span>, <span className="text-amber-400">Jaxsta</span>, <span className="text-amber-400">AllMusic</span>, <span className="text-amber-400">Genius</span>, MusicBrainz, Discogs, or IMDb as primary evidence. Either a file <em>or</em> a link is enough.
          </p>

          {/* Rights Protection — sub-category + date for Chain of Title timeline */}
          {type === "rights_protection" && (
            <div className="mt-4 border border-amber-500/20 bg-amber-500/[0.03] p-4" data-testid="rights-protection-fields">
              <div className="flex items-center gap-2 mb-3 text-[11px] font-mono-tech uppercase tracking-[0.22em] text-amber-400">
                <Scale size={12} /> Rights Protection · Chain of Title
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-500 mb-1 block font-mono-tech" htmlFor="proof-category">Proof Category</label>
                  <select
                    id="proof-category"
                    value={proofCategory}
                    onChange={(e) => setProofCategory(e.target.value)}
                    data-testid="proof-category-select"
                    className="w-full bg-[#0c0c0c] border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-amber-500"
                  >
                    {RIGHTS_CATEGORIES.map((c) => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-500 mb-1 flex items-center gap-1 font-mono-tech" htmlFor="proof-date">
                    <Calendar size={11} /> Date <span className="text-rose-400">*</span>
                  </label>
                  <input
                    id="proof-date"
                    type="date"
                    value={proofDate}
                    onChange={(e) => setProofDate(e.target.value)}
                    required
                    data-testid="proof-date-input"
                    className="w-full bg-[#0c0c0c] border border-white/15 px-3 py-2.5 text-sm font-mono-tech text-white outline-none focus:border-amber-500"
                  />
                </div>
              </div>
              <p className="mt-2 text-[11px] text-neutral-500">This date feeds your Forensic Timeline in the <span className="text-amber-400">Chain of Title</span> dashboard section.</p>
            </div>
          )}

          <label className="text-xs uppercase tracking-widest text-neutral-500 mt-4 mb-2 block font-mono-tech">4. Description (optional)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, 1000))}
            placeholder="e.g. ASCAP welcome letter dated June 2025"
            rows={2}
            data-testid="evidence-description-input"
            className="w-full bg-[#0c0c0c] border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-amber-500"
          />

          <button
            onClick={submit}
            disabled={uploading || (!file && !proofLink.trim())}
            data-testid="evidence-submit-btn"
            className="mt-3 inline-flex items-center gap-2 px-4 py-2.5 bg-amber-500 text-black hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-semibold">
            {uploading ? <><Loader2 size={14} className="animate-spin" /> Submitting…</> : <><Upload size={14} /> Submit Evidence</>}
          </button>
        </div>
      </div>

      {/* HISTORY */}
      <div className="mt-6">
        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2 font-mono-tech">Your Submissions</div>
        {loading ? (
          <div className="text-xs text-neutral-500">Loading…</div>
        ) : submissions.length === 0 ? (
          <div className="text-xs text-neutral-500" data-testid="evidence-history-empty">No submissions yet.</div>
        ) : (
          <div className="space-y-2">
            {submissions.map((s) => {
              const meta = STATUS_META[s.status] || STATUS_META.pending;
              const Icon = meta.Icon;
              return (
                <div key={s.id} data-testid={`evidence-row-${s.id}`}
                  className="p-3 bg-[#0A0A0A] border border-white/5 flex items-center gap-4">
                  <Icon size={14} className={`shrink-0 ${meta.cls}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white">
                      <span className="font-semibold capitalize">{(s.type || "").replace(/_/g, " ")}</span>
                      {s.proof_category && (
                        <span className="ml-2 text-[10px] font-mono-tech uppercase tracking-widest text-amber-400 border border-amber-500/40 bg-amber-500/[0.06] px-1.5 py-0.5">
                          {s.proof_category.replace(/_/g, " ")}
                        </span>
                      )}
                      {s.description && <span className="text-neutral-400"> · {s.description}</span>}
                    </div>
                    <div className="text-xs font-mono-tech text-neutral-500 mt-0.5 flex items-center gap-2 flex-wrap">
                      <span>{new Date(s.created_at).toLocaleString()}</span>
                      {s.proof_date && <span>· dated {s.proof_date}</span>}
                      {s.file_name && <span>· {s.file_name}</span>}
                      {s.proof_link && (
                        <a href={s.proof_link} target="_blank" rel="noopener noreferrer"
                           className="text-amber-400 hover:text-amber-300 flex items-center gap-1"
                           data-testid={`evidence-proof-link-${s.id}`}>
                          <Link2 size={11} /> Proof Link
                        </a>
                      )}
                    </div>
                    {s.reviewer_notes && (
                      <div className="text-[13px] text-neutral-400 mt-1 italic">Reviewer: {s.reviewer_notes}</div>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    {s.file_url ? (
                      <a href={s.file_url} target="_blank" rel="noopener noreferrer"
                        data-testid={`evidence-view-${s.id}`}
                        className="text-xs text-amber-500 hover:underline font-mono-tech uppercase tracking-widest">
                        View
                      </a>
                    ) : (
                      <span className="text-xs text-neutral-500 font-mono-tech uppercase tracking-widest">Link only</span>
                    )}
                    <div className={`text-xs font-mono-tech uppercase tracking-widest ${meta.cls} mt-1`}>{meta.label}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* CHAIN OF TITLE — Forensic Timeline for Rights Protection uploads.
          Only rendered inside the hardened Sovereign Vault (`/vault`);
          the timeline is class-A confidential and must never surface
          in the RoyAI evidence embed. */}
      {showChainOfTitle && <ChainOfTitle submissions={submissions} />}
    </div>
  );
}

const RIGHTS_CATEGORY_LABEL = {
  daw_creation: "DAW Creation",
  social_disclosure: "Social Disclosure",
  copyright_filing: "Copyright Filing",
  commercial_isrc: "Commercial ISRC",
};

function ChainOfTitle({ submissions }) {
  const items = (submissions || [])
    .filter((s) => s.type === "rights_protection")
    .slice()
    .sort((a, b) => (a.proof_date || "").localeCompare(b.proof_date || ""));
  if (items.length === 0) return null;
  return (
    <div className="mt-8 border border-amber-500/25 bg-gradient-to-b from-amber-500/[0.03] to-black/40 p-5 lg:p-6" data-testid="chain-of-title">
      <div className="flex items-center gap-2 mb-4">
        <Scale size={14} className="text-amber-400" />
        <div>
          <div className="text-overline text-amber-500">Chain of Title</div>
          <h3 className="font-display text-lg text-white">Forensic Timeline</h3>
        </div>
      </div>
      <ol className="relative border-l border-amber-500/30 pl-5 space-y-4" data-testid="chain-of-title-list">
        {items.map((s) => {
          const meta = STATUS_META[s.status] || STATUS_META.pending;
          return (
            <li key={s.id} className="relative" data-testid={`chain-item-${s.id}`}>
              <span className="absolute -left-[27px] top-1.5 w-3 h-3 rounded-full border-2 border-black" style={{ background: s.status === "approved" ? "#10B981" : "#F5C842" }} />
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="font-mono-tech text-xs text-amber-400 uppercase tracking-widest">
                  {s.proof_date || "—"}
                </span>
                <span className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-400 border border-white/10 bg-black/50 px-1.5 py-0.5">
                  {RIGHTS_CATEGORY_LABEL[s.proof_category] || s.proof_category}
                </span>
                <span className={`text-[10px] font-mono-tech uppercase tracking-widest ${meta.cls}`}>{meta.label}</span>
              </div>
              <div className="text-sm text-white">{s.description || "(no description)"}</div>
              <div className="text-[11px] font-mono-tech text-neutral-500 mt-1 flex items-center gap-2 flex-wrap">
                {s.file_url && (
                  <a href={s.file_url} target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300">View file</a>
                )}
                {s.proof_link && (
                  <a href={s.proof_link} target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 flex items-center gap-1">
                    <Link2 size={11} /> Proof Link
                  </a>
                )}
                <span>· submitted {new Date(s.created_at).toLocaleDateString()}</span>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
